import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getExecutiveDashboard } from '@/api/endpoints/analytics.api';
import { dashboardApi } from '@/api/endpoints/dashboard.api';
import { patientsApi } from '@/api/endpoints/patients.api';
import { alertsApi } from '@/api/endpoints/alerts.api';
import { AlertStatus } from '@/types/alert.types';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

/**
 * Hook para prefetch de dados críticos do dashboard.
 * Carrega dados em background para reduzir tempo de espera.
 */
export function usePrefetchDashboardData() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Prefetch consolidated dashboard data (highest priority - single request)
    void queryClient.prefetchQuery({
      queryKey: ['dashboard', 'all'],
      queryFn: () => dashboardApi.getAll(),
      staleTime: 1000 * 60 * 5, // 5 minutes (matches backend cache)
    });

    // Prefetch executive dashboard (3 month range - most common)
    const endDate = endOfMonth(new Date());
    const startDate = startOfMonth(subMonths(new Date(), 2));
    const dateRange = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    };

    // Prefetch in background (low priority)
    void queryClient.prefetchQuery({
      queryKey: ['executive-dashboard', dateRange],
      queryFn: () => getExecutiveDashboard(dateRange),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Prefetch patients list (first page)
    void queryClient.prefetchQuery({
      queryKey: ['patients', { page: 0, size: 20 }],
      queryFn: () => patientsApi.getAll({ page: 0, size: 20 }),
      staleTime: 1000 * 60 * 5,
    });

    // Prefetch pending alerts count
    void queryClient.prefetchQuery({
      queryKey: ['alerts', 'pending'],
      queryFn: () => alertsApi.getAll(AlertStatus.PENDING, { page: 0, size: 10 }),
      staleTime: 1000 * 60 * 2, // 2 minutes for alerts
    });
  }, [queryClient]);
}
